import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { Field, formValueSelector, getFormSyncErrors, getFormMeta, hasSubmitFailed, change, touch } from "redux-form";
import moment from "moment";
import Icon from "../../../../icons";
import {
  FORM_FIELDS,
  VALIDATION_MESSAGES,
  ADJUSTER_LICENSE_STATES,
  VALIDATION_FIELD_PREFIXES,
} from "../constants";
import FormTextInput from "../../../FormComponents/formTextInput";
import DatePicker from "../../../DatePicker";
import FormSelect from "../../../FormComponents/formSelect";
import SupervisorSelect from "./SupervisorSelect";
import { useUsernameCheck } from "../hooks/useUsernameCheck";
import useFocusDropdown from "../hooks/useFocusDropdown";
import { useModules } from "../../../../utils/modules";
import {
  required,
  email,
  composeValidators,
  minLength,
  alphanumeric,
} from "../utils/formValidators";

const EMAIL_VALIDATORS = composeValidators(required, email);
const USERNAME_VALIDATORS = composeValidators(
  required,
  minLength(6),
  alphanumeric,
);

const LICENSE_FIELD_NAMES = ADJUSTER_LICENSE_STATES.flatMap((s) => [
  `${VALIDATION_FIELD_PREFIXES.LICENSE_NUMBER}${s.state_code}`,
  `${VALIDATION_FIELD_PREFIXES.LICENSE_EXPIRATION}${s.state_code}`,
]);

const BasicInfo = ({
  formName,
  isEditMode,
  dispatch,
  supervisorOptions,
  onSupervisorScrollEnd,
  onSupervisorSearch,
  supervisorsLoading,
  supervisorError,
}) => {
  const { isModuleEnabled } = useModules();
  const [licenseFields, setLicenseFields] = useState({});
  const { checking, result, error: usernameError, checkUsername, reset: resetUsernameCheck, usernameTouched } = useUsernameCheck();

  const selector = useMemo(() => formValueSelector(formName), [formName]);
  const supervisorId = useSelector((state) => selector(state, "supervisor_id"));
  const jobRole = useSelector((state) => selector(state, "job_role"));
  const tempUser = useSelector((state) => selector(state, "temp_user"));
  const adjusterLicenseStates = useSelector((state) => selector(state, "adjuster_license_states"));
  const allFormValues = useSelector(
    (state) => selector(state, ...LICENSE_FIELD_NAMES),
    shallowEqual,
  );
  const formSyncErrors = useSelector(getFormSyncErrors(formName)) || {};
  const formMeta = useSelector(getFormMeta(formName)) || {};
  const submitFailed = useSelector(hasSubmitFailed(formName));

  // License dropdown configuration
  const licenseOptions = useMemo(
    () =>
      ADJUSTER_LICENSE_STATES.map((s) => ({
        id: s.state_code,
        text: s.state_code,
        fullName: `${s.state_code} (${s.state_name})`,
      })),
    [],
  );

  const licenseDropdown = useFocusDropdown({
    options: licenseOptions,
    multiSelect: true,
    onSelectionChange: (selected) => {
      const newFields = { ...licenseFields };

      Object.keys(newFields).forEach((stateCode) => {
        if (!selected.some((item) => item.id === stateCode)) {
          delete newFields[stateCode];
          dispatch(change(formName, `${VALIDATION_FIELD_PREFIXES.LICENSE_NUMBER}${stateCode}`, ""));
          dispatch(change(formName, `${VALIDATION_FIELD_PREFIXES.LICENSE_EXPIRATION}${stateCode}`, ""));
        }
      });

      selected.forEach((item) => {
        if (!newFields[item.id]) {
          newFields[item.id] = { licenseNumber: "", expirationDate: "" };
        }
      });

      setLicenseFields(newFields);
      dispatch(
        change(
          formName,
          "adjuster_license_states",
          selected.map((s) => s.id),
        ),
      );
    },
  });

  // Job role dropdown configuration
  const jobRoleOptions = useMemo(
    () =>
      [
        { value: "", label: "Select Job Role" },
        { value: "CSR", label: "CSR" },
        { value: "Case Manager", label: "Case Manager" },
        { value: "Director", label: "Director" },
        { value: "Examiner", label: "Examiner" },
        { value: "Field Adjuster", label: "Field Adjuster" },
        { value: "Litigation Director", label: "Litigation Director" },
        { value: "Litigation Specialist", label: "Litigation Specialist" },
        { value: "Litigation VP", label: "Litigation VP" },
        { value: "Manager", label: "Manager" },
        { value: "Specialist", label: "Specialist" },
        { value: "Supervisor", label: "Supervisor" },
        { value: "Technical Team Lead", label: "Technical Team Lead" },
        { value: "Vice President", label: "Vice President" },
      ].map((option) => ({
        id: option.value,
        text: option.label,
      })),
    [],
  );

  const jobRoleDropdown = useFocusDropdown({
    options: jobRoleOptions.slice(1),
    multiSelect: true,
    onSelectionChange: (selected) => {
      const values = selected.map((s) => s.id);
      dispatch(change(formName, "job_role", values));
    },
  });

  // Initialize job role dropdown from form value
  useEffect(() => {
    const currentIds = jobRoleDropdown.selectedItems.map((s) => s.id).sort().join(',');

    if (Array.isArray(jobRole) && jobRole.length > 0) {
      const formIds = [...jobRole].sort().join(',');
      if (currentIds !== formIds) {
        const selected = jobRole
          .map((id) => jobRoleOptions.find((opt) => opt.id?.trim().toLowerCase() === id?.trim().toLowerCase()))
          .filter(Boolean);
        jobRoleDropdown.setSelectedItems(selected);
      }
    } else if (currentIds !== '') {
      jobRoleDropdown.setSelectedItems([]);
    }
  }, [jobRole]);

  // Initialize license dropdown + local license fields from form values (edit mode)
  useEffect(() => {
    if (!Array.isArray(adjusterLicenseStates) || adjusterLicenseStates.length === 0) return;

    const currentIds = licenseDropdown.selectedItems.map((s) => s.id).sort().join(',');
    const formIds = [...adjusterLicenseStates].sort().join(',');
    if (currentIds !== formIds) {
      const selected = adjusterLicenseStates
        .map((code) => licenseOptions.find((opt) => opt.id === code))
        .filter(Boolean);
      licenseDropdown.setSelectedItems(selected);
    }

    // Sync local licenseFields with per-state form values
    const nextFields = {};
    adjusterLicenseStates.forEach((code) => {
      const number = allFormValues[`${VALIDATION_FIELD_PREFIXES.LICENSE_NUMBER}${code}`] || "";
      const expiration = allFormValues[`${VALIDATION_FIELD_PREFIXES.LICENSE_EXPIRATION}${code}`] || "";
      nextFields[code] = { licenseNumber: number, expirationDate: expiration };
    });
    setLicenseFields((prev) => {
      // Avoid useless updates
      const prevKeys = Object.keys(prev).sort().join(',');
      const nextKeys = Object.keys(nextFields).sort().join(',');
      if (prevKeys === nextKeys) {
        const same = Object.keys(nextFields).every((k) =>
          prev[k]?.licenseNumber === nextFields[k].licenseNumber &&
          prev[k]?.expirationDate === nextFields[k].expirationDate
        );
        if (same) return prev;
      }
      return nextFields;
    });
  }, [adjusterLicenseStates]);

  const tempUserOptions = [
    { value: false, label: "No" },
    { value: true, label: "Yes" },
  ];

  const handleUsernameBlur = useCallback((e) => {
    const username = e.target?.value;
    if (username && username.length >= 6 && !isEditMode) {
      checkUsername(username);
    }
  }, [isEditMode, checkUsername]);

  const handleUsernameKeyUp = useCallback(() => {
    resetUsernameCheck();
  }, [resetUsernameCheck]);

  const handleTempUserChange = () => { };

  const handleLicenseFieldChange = (stateCode, field, value) => {
    setLicenseFields((prev) => ({
      ...prev,
      [stateCode]: {
        ...prev[stateCode],
        [field]: value,
      },
    }));

    const reduxKey =
      field === "licenseNumber"
        ? `${VALIDATION_FIELD_PREFIXES.LICENSE_NUMBER}${stateCode}`
        : `${VALIDATION_FIELD_PREFIXES.LICENSE_EXPIRATION}${stateCode}`;
    dispatch(change(formName, reduxKey, value));
  };

  const removeLicense = (stateCode) => {
    licenseDropdown.removeItem(
      licenseDropdown.selectedItems.findIndex((item) => item.id === stateCode),
    );
  };

  const getUsernameMessage = () => {
    if (!usernameTouched) return null;
    if (checking) {
      return { type: 'info', message: 'Checking username availability...' };
    }
    if (usernameError) {
      return { type: 'error', message: usernameError };
    }
    if (result) {
      return {
        type: result.available ? 'success' : 'error',
        message: result.message || (result.available ? VALIDATION_MESSAGES.USERNAME_AVAILABLE : VALIDATION_MESSAGES.USERNAME_UNAVAILABLE)
      };
    }
    return null;
  };

  const usernameMessage = getUsernameMessage();

  return (
    <>
      {isModuleEnabled("admin.user.add_user.name") && (
        <div className="wth-12 formRow formGroup" data-test="form-name-block">
          <div className="mrNone wth-12 rowFlex">
            <label data-test="form-name-label">
              Customer Name
              <sup>*</sup>
            </label>
            <span className="quickTooltip-bottom quickTooltip medium alignMiddle fast fadeInLeftSmall" data-quicktooltip="Name of the User. This is one of the key pieces of information used for searching. Please make sure you spell it correctly">
              <Icon name="info" className="icon ml greyInfoIcon" />
            </span>
          </div>
          <div className="rowFlex">
            <div
              className="colFlex wth-4"
              data-test="form-first-name"
            >
              <Field
                name={FORM_FIELDS.FIRST_NAME}
                component={FormTextInput}
                isRequired={true}
                validate={required}
                placeholder="First name"
                formGroup=""
                description=""
              />
            </div>
            <div
              className="colFlex wth-4"
              data-test="form-middle-name"
            >
              <Field
                name={FORM_FIELDS.MIDDLE_NAME}
                component={FormTextInput}
                placeholder="Middle name"
                formGroup=""
              />
            </div>
            <div
              className="colFlex wth-4"
              data-test="form-last-name"
            >
              <Field
                name={FORM_FIELDS.LAST_NAME}
                component={FormTextInput}
                placeholder="Last name"
                formGroup=""
              />
            </div>
          </div>
        </div>)}
      <div className="wth-12 formRow rowFlex formGroup">
        {isModuleEnabled("admin.user.add_user.phone") && (
          <div className="colFlex wth-6 mrNone">
            <div className="rowFlex alignMiddle">
              <label>
                Phone Number
              </label>
              <span className="quickTooltip-bottom quickTooltip medium alignMiddle fast fadeInLeftSmall" data-quicktooltip="Phone number of the user will be used for contact purpose">
                <Icon name="info" className="icon ml greyInfoIcon" />
              </span>
            </div>
            <Field
              name={FORM_FIELDS.PHONE}
              component={FormTextInput}
              placeholder="Phone Number"
              formGroup=""
              description=""
            />
          </div>)}
        {isModuleEnabled("admin.user.add_user.email") && (
          <div className="colFlex wth-6 mrNone">
            <div className="rowFlex alignMiddle">
              <label>
                Email ID<sup>*</sup>
              </label>
              <span className="quickTooltip-bottom quickTooltip medium alignMiddle fast fadeInLeftSmall" data-quicktooltip="Email address of the user will be used for contact purpose">
                <Icon name="info" className="icon ml greyInfoIcon" />
              </span>
            </div>
            <Field
              name={FORM_FIELDS.EMAIL}
              component={FormTextInput}
              isRequired={true}
              validate={EMAIL_VALIDATORS}
              placeholder="Email ID"
              formGroup=""
              description=""
            />
          </div>)}
      </div>
      <div className="wth-12 formRow rowFlex formGroup">
        {isModuleEnabled("admin.user.add_user.job_title") && (
          <div className="colFlex wth-6 mrNone">
            <div className="rowFlex alignMiddle">
              <label>
                Job Title
              </label>
              <span className="quickTooltip-bottom quickTooltip medium alignMiddle fast fadeInLeftSmall" data-quicktooltip="Job title of the user will be used for contact purpose">
                <Icon name="info" className="icon ml greyInfoIcon" />
              </span>
            </div>
            <Field
              name={FORM_FIELDS.JOB_TITLE}
              component={FormTextInput}
              placeholder="Job Title"
              formGroup=""
              dataTest="form-job-title-label"
              description=""
            />
          </div>)}
        {isModuleEnabled("admin.user.add_user.username") && (
          <div className="colFlex wth-6 mrNone">
            <div className="rowFlex alignMiddle">
              <label>
                Username<sup>*</sup>
              </label>
              <span className="quickTooltip-bottom quickTooltip medium alignMiddle fast fadeInLeftSmall" data-quicktooltip="Username that can be used for login purpose">
                <Icon name="info" className="icon ml greyInfoIcon" />
              </span>
            </div>
            <Field
              name={FORM_FIELDS.USERNAME}
              component={FormTextInput}
              isRequired={true}
              validate={USERNAME_VALIDATORS}
              placeholder="Username"
              formGroup=""
              isDisabled={isEditMode}
              helpText={
                isEditMode
                  ? "Username cannot be changed"
                  : "Minimum 6 characters, letters and numbers only"
              }
              onKeyUp={handleUsernameKeyUp}
              onFieldValueChange={handleUsernameBlur}
              description=""
            />
            {usernameMessage && (
              <small className={`fieldDescription usernameMessage-${usernameMessage.type}`}>
                {usernameMessage.message}
              </small>
            )}
          </div>)}
      </div>
      <div className="wth-12 formRow rowFlex formGroup">
        {isModuleEnabled("admin.user.add_user.supervisor") && (
          <div className="colFlex wth-6 mrNone">
            <div className="rowFlex alignMiddle">
              <label>
                Supervisor<sup>*</sup>
              </label>
              <span className="quickTooltip-bottom quickTooltip medium alignMiddle fast fadeInLeftSmall" data-quicktooltip="Select the Supervisor for this user.">
                <Icon name="info" className="icon ml greyInfoIcon" />
              </span>
            </div>
            <Field
              name={FORM_FIELDS.SUPERVISOR_ID}
              component={SupervisorSelect}
              options={supervisorOptions}
              title="Select the Supervisor for this user."
              selected={supervisorId}
              onScrollEnd={onSupervisorScrollEnd}
              onSearch={onSupervisorSearch}
              isLoadingMore={supervisorsLoading}
              errorMessage={supervisorError}
            />
            <small className="fieldDescription"></small>
          </div>)}
        {isModuleEnabled("admin.user.add_user.temporary_user") && (
          <div className="colFlex wth-6">
            <div className="rowFlex alignMiddle">
              <label>
                Temporary User<sup>*</sup>
              </label>
              {/* <span className="quickTooltip-bottom alignMiddle fast fadeInLeftSmall" data-quicktooltip="">
                <Icon name="info" className="icon ml greyInfoIcon" />
              </span> */}
            </div>
            <Field
              name={FORM_FIELDS.TEMP_USER}
              component={FormSelect}
              options={tempUserOptions}
              formGroup=""
              selected={tempUser}
              placeholder="Temporary User"
              onChange={handleTempUserChange}
            />
          </div>)}
      </div>
      <div className="wth-12 formRow rowFlex">
        {isModuleEnabled("admin.user.add_user.job_role") && (() => {
          const jobRoleError = (formMeta.job_role?.touched || submitFailed) && formSyncErrors.job_role;
          return (
            <div className={`formGroup colFlex wth-6 formTags${jobRoleError ? ' errorMsg' : ''}`}>
              <label>
                Job Role
              </label>
              {/* <span className="quickTooltip-bottom alignMiddle fast fadeInLeftSmall" data-quicktooltip="">
              <Icon name="info" className="icon ml greyInfoIcon" />
            </span> */}
              <div
                ref={jobRoleDropdown.containerRef}
                onMouseDown={jobRoleDropdown.handleContainerMouseDown}
              >
                <div className="ReactTags__tags react-tags-wrapper">
                  <div
                    className="ReactTags__selected"
                    onBlur={() => dispatch(touch(formName, "job_role"))}
                    onClick={jobRoleDropdown.handleContainerClick}
                  >
                    {jobRoleDropdown.selectedItems.map((item, idx) => (
                      <span key={item.id} className="tag-wrapper ReactTags__tag">
                        {item.text}
                        <span
                          className="ReactTags__remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            jobRoleDropdown.removeItem(idx);
                          }}
                        >
                          <Icon name="close" className="icon" />
                        </span>
                      </span>
                    ))}
                    <div className="ReactTags__tagInput">
                      <input
                        ref={jobRoleDropdown.inputRef}
                        className="ReactTags__tagInputField"
                        type="text"
                        placeholder={
                          jobRoleDropdown.selectedItems.length === 0
                            ? "Select Job Role"
                            : ""
                        }
                        value={jobRoleDropdown.inputValue}
                        onChange={(e) =>
                          jobRoleDropdown.handleInputChange(e.target.value)
                        }
                        onKeyDown={jobRoleDropdown.handleKeyDown}
                        onFocus={jobRoleDropdown.handleInputFocus}
                        onBlur={jobRoleDropdown.handleInputBlur}
                      />
                    </div>
                  </div>
                </div>
                {jobRoleDropdown.isOpen &&
                  jobRoleDropdown.filteredOptions.length > 0 && (
                    <div className="mailDropdown select serviceLocation">
                      <div className="mailingSearch serviceLocation openTop onClick">
                        <div className="select2-results">
                          {jobRoleDropdown.filteredOptions.map(
                            (item, index) => (
                              <a
                                className={
                                  jobRoleDropdown.highlightedIndex === index
                                    ? "activeItem"
                                    : ""
                                }
                                key={item.id}
                                onClick={() =>
                                  jobRoleDropdown.selectOption(item)
                                }
                                onMouseEnter={() =>
                                  jobRoleDropdown.setHighlightedIndex(index)
                                }
                                onMouseLeave={() =>
                                  jobRoleDropdown.setHighlightedIndex(null)
                                }
                              >
                                <div className="drop-down-block">
                                  <span className="skillset-name">
                                    {item.text}
                                  </span>
                                </div>
                              </a>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
              {jobRoleError && <span className="errorMsg">{jobRoleError}</span>}
            </div>
          );
        })()}
        {isModuleEnabled("admin.user.add_user.ee_code") && (
          <div className="formGroup colFlex wth-6 rowFlex formTags">
            <label>
              EE Code
            </label>
            {/* <span className="quickTooltip-bottom alignMiddle fast fadeInLeftSmall" data-quicktooltip="">
              <Icon name="info" className="icon ml greyInfoIcon" />
            </span> */}
            <Field
              name={FORM_FIELDS.EE_CODE}
              component={FormTextInput}
              placeholder="EE Code*"

            />
          </div>)}
      </div>

      {/* Adjuster License number with focus-based dropdown */}
      {isModuleEnabled("admin.user.add_user.adjuster_license_number") && (<>
        <div className="formGroup" data-test="form-license-block">
          <div className="wth-12">
            <div className="formGroup mrNone wth-12">
              <label>
                Adjuster License Number
                {/* <span className="quickTooltip-bottom alignMiddle fast fadeInLeftSmall" data-quicktooltip="Name of the User. This is one of the key pieces of information used for searching. Please make sure you spell it correctly">
              <Icon name="info" className="icon ml greyInfoIcon" />
            </span> */}
              </label>
            </div>
            <div
              ref={licenseDropdown.containerRef}
              onMouseDown={licenseDropdown.handleContainerMouseDown}
            >
              <div className="formGroup mrNone formTags">
                <div className="ReactTags__tags react-tags-wrapper">
                  <div
                    className="ReactTags__selected"
                    onClick={licenseDropdown.handleContainerClick}
                  >
                    {licenseDropdown.selectedItems.map((item, index) => (
                      <span
                        key={item.id}
                        className="tag-wrapper ReactTags__tag"
                      >
                        {item.text}
                        <span
                          className="ReactTags__remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            licenseDropdown.removeItem(index);
                          }}
                        >
                          <Icon name="close" className="icon" />
                        </span>
                      </span>
                    ))}
                    <div className="ReactTags__tagInput">
                      <input
                        ref={licenseDropdown.inputRef}
                        className="ReactTags__tagInputField"
                        type="text"
                        placeholder={
                          licenseDropdown.selectedItems.length === 0
                            ? "Choose License States"
                            : ""
                        }
                        value={licenseDropdown.inputValue}
                        onChange={(e) =>
                          licenseDropdown.handleInputChange(e.target.value)
                        }
                        onKeyDown={licenseDropdown.handleKeyDown}
                        onFocus={licenseDropdown.handleInputFocus}
                        onBlur={licenseDropdown.handleInputBlur}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {licenseDropdown.isOpen &&
                licenseDropdown.filteredOptions.length > 0 && (
                  <div className="mailDropdown select serviceLocation">
                    <div className="mailingSearch serviceLocation openTop onClick">
                      <div className="select2-results" id="licenseTags">
                        {licenseDropdown.filteredOptions.map(
                          (item, index) => (
                            <a
                              className={
                                licenseDropdown.highlightedIndex === index
                                  ? "activeItem"
                                  : ""
                              }
                              key={item.id}
                              onClick={() =>
                                licenseDropdown.selectOption(item)
                              }
                              onMouseEnter={() =>
                                licenseDropdown.setHighlightedIndex(index)
                              }
                              onMouseLeave={() =>
                                licenseDropdown.setHighlightedIndex(null)
                              }
                            >
                              <div className="drop-down-block">
                                <span className="skillset-name">
                                  {item.text}
                                </span>
                              </div>
                            </a>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Dynamic License Fields */}
        {licenseDropdown.selectedItems.map((license) => {
          const numberKey = `${VALIDATION_FIELD_PREFIXES.LICENSE_NUMBER}${license.id}`;
          const expirationKey = `${VALIDATION_FIELD_PREFIXES.LICENSE_EXPIRATION}${license.id}`;
          const numberError =
            (formMeta[numberKey]?.touched || submitFailed) && formSyncErrors[numberKey];
          const expirationError =
            (formMeta[expirationKey]?.touched || submitFailed) && formSyncErrors[expirationKey];

          return (
            <div key={license.id} className="wth-12 formRow rowFlex license-number">
              <div className="formGroup colFlex wth-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Enter ${license.id} license number`}
                  value={licenseFields[license.id]?.licenseNumber || ""}
                  onChange={(e) =>
                    handleLicenseFieldChange(
                      license.id,
                      "licenseNumber",
                      e.target.value,
                    )
                  }
                  onBlur={() => dispatch(touch(formName, numberKey))}
                />
                {numberError && (
                  <span className="errorMsg">{numberError}</span>
                )}
              </div>
              <div className="formGroup colFlex wth-5 formTags">
                <DatePicker
                  id={expirationKey}
                  inputClass="form-control"
                  selectedDate={licenseFields[license.id]?.expirationDate || ""}
                  placeholderText={`Enter ${license.id} expiration date`}
                  showCalenderIcon={true}
                  shouldCloseOnSelect={true}
                  onChange={(date) => {
                    const formattedDate =
                      date && moment(date).isValid()
                        ? moment(date).format("YYYY-MM-DD")
                        : "";
                    handleLicenseFieldChange(
                      license.id,
                      "expirationDate",
                      formattedDate,
                    );
                  }}
                  onCalendarClose={() => dispatch(touch(formName, expirationKey))}
                />
                {expirationError && (
                  <span className="errorMsg">{expirationError}</span>
                )}
              </div>
              <div className="colFlex wth-1">
                <button
                  type="button"
                  className="btn-cc__link"
                  onClick={() => removeLicense(license.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          );
        })}</>)}
    </>
  );
};

export default BasicInfo;
